import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import { observer } from 'src/utils/mobx-react';
import { DateTime } from 'src_common/utils/time/time';

import { getPeriod } from 'src/utils/periodTime';
import { CalendarIconWrapper } from 'src/components/DateInput/DateInput.style';
import { ExportToCsvButton } from 'src/components/Buttons/ExportToCsvButton/ExportToCsvButton.style';
import { ExportIcon } from 'src/assets/icons/ExportIcon';
import { DateRangeFormState } from './DateRangeFormNew.state';
import { RangeType } from 'src/components/CustomersBar/CustomerTab/TransactionHistoryTab/TransactionHistoryTab.state';
import {
    DateRangeColumns,
    DateColumnsRow,
    SelectDateWrapper,
    SelectDate,
    DateInput,
    DatePickerLabel,
    DatePickerWrapper,
} from './DateRangeForm.styles';

export interface DateRangeFormPropsType {
    data: RangeType;
    isDisabled: boolean;
    handleOnUpdateDate: (startDate: Date, endDate: Date) => void;
    onGenerateCSV: () => void;
}

const fromPickerDate = (date: Date | null, userZone: string): Date | null => {
    if (date === null) return null;

    const dt = DateTime.from(date)?.tz(userZone) ?? null;
    if (dt === null) return null;

    return new Date(dt.unixMs());
};

const toPickerDate = (isoStringOrDate: Date | string | null, userZone: string): Date | null => {
    if (isoStringOrDate === null) return null;

    const dt = DateTime.from(isoStringOrDate)?.tz(userZone) ?? null;
    if (dt === null) return null;

    return new Date(dt.unixMs());
};

export const DateRangeForm = observer('DateRangeForm', (props: DateRangeFormPropsType) => {
    const { handleOnUpdateDate, onGenerateCSV, isDisabled, data } = props;
    const [state] = useState(() => new DateRangeFormState());

    const userTimeZone = DateTime.guessUserTimezone();

    const { startDate, endDate } = data;
    const time = getPeriod(startDate, endDate);
    const maxDate = new Date();

    const startDateNative = toPickerDate(data.startDate, userTimeZone);
    const endDateNative = toPickerDate(data.endDate, userTimeZone);

    const onDateChange = (newDate: Date, isStart: boolean): void => {
        const adjustedDate = fromPickerDate(newDate, userTimeZone);
        if (adjustedDate === null) return;

        if (isStart) {
            handleOnUpdateDate(adjustedDate, data.endDate);
        } else {
            handleOnUpdateDate(data.startDate, adjustedDate);
        }
    };

    return (
        <DateColumnsRow>
            <DateRangeColumns>
                <DateInput className='date-input'>
                    <CalendarIconWrapper />

                    <DatePickerWrapper isValid={state.isValidStartDate}>
                        <DatePickerLabel>From ({userTimeZone})</DatePickerLabel>
                        <DatePicker
                            onChange={(val: Date) => onDateChange(val, true)}
                            onFocus={state.setFocus(true)}
                            onBlur={state.onBlurStartDate}
                            selected={startDateNative}
                            showYearDropdown
                            showTimeInput
                            timeInputLabel='Time:'
                            dateFormat='yyyy-MM-dd HH:mm'
                            maxDate={endDateNative}
                        />
                    </DatePickerWrapper>
                </DateInput>
            </DateRangeColumns>

            <DateRangeColumns>
                <DateInput className='date-input'>
                    <CalendarIconWrapper />
                    <DatePickerWrapper isValid={state.isValidEndDate}>
                        <DatePickerLabel>To ({userTimeZone})</DatePickerLabel>
                        <DatePicker
                            onChange={(val: Date) => onDateChange(val, false)}
                            onFocus={state.setFocus(true)}
                            onBlur={state.onBlurEndDate}
                            selected={endDateNative}
                            showYearDropdown
                            showTimeInput
                            timeInputLabel='Time:'
                            dateFormat='yyyy-MM-dd HH:mm'
                            maxDate={maxDate}
                            minDate={startDateNative}
                        />
                    </DatePickerWrapper>
                </DateInput>
            </DateRangeColumns>

            <DateRangeColumns>
                <CalendarIconWrapper />
                <SelectDateWrapper>
                    Choose a date range
                    <SelectDate
                        value={time}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>): void =>
                            state.changeTime(e.target.value, handleOnUpdateDate)
                        }
                    >
                        <option
                            value='custom'
                            style={{ display: 'none' }}
                        >
                            Custom date
                        </option>
                        <option value='today'>Today</option>
                        <option value='3-days'>Last 3 days</option>
                        <option value='7-days'>Last 7 days</option>
                        <option value='week'>This week</option>
                        <option value='month'>This month</option>
                    </SelectDate>
                </SelectDateWrapper>
            </DateRangeColumns>

            <ExportToCsvButton
                type='button'
                onClick={onGenerateCSV}
                isDisabled={isDisabled}
                data-test='export-to-csv-button'
            >
                <ExportIcon />
                Export to CSV
            </ExportToCsvButton>
        </DateColumnsRow>
    );
});
